import { Field as AField } from '@ark-ui/solid/field';
import { mergeRefs } from '@solid-primitives/refs';
import { createSignal, createEffect, useContext, Show, splitProps, children } from 'solid-js';
import { cn } from 'tailwind-variants';
import { Field, InputContext } from './field.tsx';
import { disableAutocomplete, useFormContext } from './form.tsx';
import type { ComponentProps, JSXElement } from 'solid-js';

export function Input(props: ComponentProps<typeof AField.Input>) {
	const [ref, setRef] = createSignal<HTMLInputElement>();
	const [input] = useContext(InputContext);
	const { data } = useFormContext();

	createEffect(() => {
		const el = ref();
		if (props.autofocus && el) {
			setTimeout(() => el && el.focus());
		}
	});

	return (
		<AField.Input
			tabindex={0}
			{...input}
			{...props}
			{...disableAutocomplete(props)}
			autocapitalize={
				props.autocapitalize ?? (props.inputMode === 'email' || props.type === 'password' ? 'none' : undefined)
			}
			ref={mergeRefs(props.ref, setRef)}
			aria-readonly={props.readonly || data().pending}
			readonly={props.readonly || data().pending}
			onClick={(e: MouseEvent) => {
				if (props.readonly) {
					(e.currentTarget as HTMLInputElement)?.select();
					return;
				}
				// @ts-expect-error why are these so complex
				props.onClick?.(e);
			}}
			class={cn(
				'w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 focus-visible:ring-4 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-zinc-50 disabled:bg-zinc-100 disabled:text-zinc-500 aria-invalid:border-rose-600 aria-readonly:cursor-not-allowed aria-readonly:bg-zinc-100 aria-readonly:text-zinc-500 data-com-onepassword-filled:bg-clip-text motion-safe:disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:focus-visible:ring-offset-zinc-950 dark:disabled:bg-zinc-800 dark:aria-readonly:bg-zinc-900',
				props.class,
				props.type === 'time' && 'tabular-nums',
			)}
		/>
	);
}

export function InputField(
	props: ComponentProps<typeof Input> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	const description = children(() => other.description);
	return (
		<Field name={rest.name ?? ''}>
			<Field.Label>{other.label}</Field.Label>
			<Input {...rest} />
			<Field.Errors />
			<Show when={description()}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Field>
	);
}

export function Textarea(props: ComponentProps<typeof AField.Textarea>) {
	const [ref, setRef] = createSignal<HTMLTextAreaElement>();
	const [input] = useContext(InputContext);
	const { data } = useFormContext();

	createEffect(() => {
		const el = ref();
		if (props.autofocus && el) {
			setTimeout(() => el && el.focus());
		}
	});

	return (
		<AField.Textarea
			autoresize
			tabindex={0}
			{...input}
			{...props}
			ref={mergeRefs(props.ref, setRef)}
			aria-readonly={props.readonly || data().pending}
			readonly={props.readonly || data().pending}
			onClick={(e: MouseEvent) => {
				if (props.readonly) {
					(e.currentTarget as HTMLInputElement)?.select();
					return;
				}
				// @ts-expect-error why are these so complex
				props.onClick?.(e);
			}}
			class={cn(
				'w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 focus-visible:ring-4 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-zinc-50 disabled:bg-zinc-100 disabled:text-zinc-500 aria-invalid:border-rose-600 aria-readonly:cursor-not-allowed aria-readonly:bg-zinc-100 aria-readonly:text-zinc-500 data-com-onepassword-filled:bg-clip-text motion-safe:disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:focus-visible:ring-offset-zinc-950 dark:disabled:bg-zinc-800 dark:aria-readonly:bg-zinc-900',
				props.class,
			)}
		/>
	);
}

export function TextareaField(
	props: ComponentProps<typeof AField.Textarea> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	const description = children(() => other.description);
	return (
		<Field name={rest.name ?? ''}>
			<Field.Label>{other.label}</Field.Label>
			<Textarea {...rest} />
			<Field.Errors />
			<Show when={description()}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Field>
	);
}
